import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Building2, Briefcase, Check, ChevronDown, Plus, Clock, Shield } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/core/auth/AuthProvider";
import { useNavigate } from "react-router-dom";

const RECENT_KEY = "recentClientSelections";
const MAX_RECENT = 5;

interface RecentSelection {
  clientId: string;
  clientName: string;
  orgId: string;
  orgName: string;
  timestamp: number;
}

function getRecentSelections(): RecentSelection[] {
  try {
    const stored = localStorage.getItem(RECENT_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as RecentSelection[];
  } catch {
    return [];
  }
}

function addRecentSelection(selection: Omit<RecentSelection, "timestamp">) {
  const recent = getRecentSelections();
  const filtered = recent.filter((r) => r.clientId !== selection.clientId);
  const updated = [{ ...selection, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
}


export function ContextPicker() {
  const { 
    currentOrg, setCurrentOrg, currentClient, setCurrentClient, 
    organizations, clients, setAdminMode, setImpersonatedRole 
  } = useTenant();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [focusedOrgIndex, setFocusedOrgIndex] = useState(-1);
  const [focusedClientIndex, setFocusedClientIndex] = useState(-1);
  const [activePanel, setActivePanel] = useState<'org' | 'client'>('org');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const visibleOrganizations = useMemo(
    () => organizations.filter((org) => org.type === "agency"),
    [organizations]
  );

  const agencyClients = useMemo(() => {
    if (!currentOrg) return [];
    return clients.filter((c) => c.org_id === currentOrg.id);
  }, [clients, currentOrg]);

  const filteredClients = useMemo(() => {
    if (!clientSearch) return agencyClients;
    return agencyClients.filter((c) =>
      c.name.toLowerCase().includes(clientSearch.toLowerCase())
    );
  }, [agencyClients, clientSearch]);

  const recentSelections = useMemo(() => {
    const recent = getRecentSelections();
    return recent.filter((r) =>
      clients.some((c) => c.id === r.clientId) &&
      organizations.some((o) => o.id === r.orgId)
    );
  }, [clients, organizations]);

  const handleOrgSelect = useCallback((org: { id: string; name: string }) => {
    setCurrentOrg(org);
    setClientSearch("");
    setActivePanel('client');
    setFocusedClientIndex(0);
  }, [setCurrentOrg]);

  const handleClientSelect = useCallback((client: { id: string; name: string; org_id?: string }) => {
    const org = organizations.find((o) => o.id === (client.org_id || currentOrg?.id));
    if (org) {
      addRecentSelection({
        clientId: client.id,
        clientName: client.name,
        orgId: org.id,
        orgName: org.name,
      });
    }
    setCurrentClient(client);
    if (hasRole("admin")) setAdminMode(false);
    setClientSearch("");
    setOpen(false);
  }, [setCurrentClient, hasRole, setAdminMode, organizations, currentOrg]);

  const handleRecentSelect = useCallback((selection: RecentSelection) => {
    const org = organizations.find((o) => o.id === selection.orgId);
    const client = clients.find((c) => c.id === selection.clientId);
    if (org && client) {
      setCurrentOrg(org);
      handleClientSelect(client);
    }
  }, [organizations, clients, setCurrentOrg, handleClientSelect]);

  const handleAdminMode = useCallback(() => {
    setImpersonatedRole('admin');
    setAdminMode(true);
    setOpen(false);
  }, [setImpersonatedRole, setAdminMode]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setClientSearch("");
      setFocusedOrgIndex(-1);
      setFocusedClientIndex(-1);
      setActivePanel('org');
    } else {
      const currentOrgIdx = visibleOrganizations.findIndex((o) => o.id === currentOrg?.id);
      setFocusedOrgIndex(currentOrgIdx >= 0 ? currentOrgIdx : 0);
    }
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (activePanel === 'org') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedOrgIndex((i) => Math.min(i + 1, visibleOrganizations.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedOrgIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && focusedOrgIndex >= 0) {
        e.preventDefault();
        handleOrgSelect(visibleOrganizations[focusedOrgIndex]);
      } else if (e.key === 'ArrowRight' || e.key === 'Tab') {
        if (currentOrg && agencyClients.length > 0) {
          e.preventDefault();
          setActivePanel('client');
          setFocusedClientIndex(0);
          searchInputRef.current?.focus();
        }
      }
    } else {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedClientIndex((i) => Math.min(i + 1, filteredClients.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedClientIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && focusedClientIndex >= 0) {
        e.preventDefault();
        handleClientSelect(filteredClients[focusedClientIndex]);
      } else if (e.key === 'ArrowLeft' && !clientSearch) {
        e.preventDefault();
        setActivePanel('org');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    }
  }, [
    activePanel, visibleOrganizations, focusedOrgIndex, currentOrg, 
    agencyClients, filteredClients, focusedClientIndex, clientSearch,
    handleOrgSelect, handleClientSelect
  ]);

  useEffect(() => {
    if (!open) return;
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [open]);

  if (visibleOrganizations.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 h-10 md:h-9 px-2.5 md:px-3 min-w-0 max-w-[280px] sm:max-w-[320px] justify-start"
        >
          <Building2
            className={`h-4 w-4 shrink-0 ${
              currentOrg ? "text-primary" : "text-muted-foreground"
            }`}
          />
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="truncate w-full text-sm font-medium block">
              {currentClient?.name ?? (currentOrg ? "Select Client" : "Select Agency")}
            </span>
            {currentOrg && (
              <span className="truncate w-full text-xs text-muted-foreground block">
                {currentOrg.name}
              </span>
            )}
          </div>
          <ChevronDown className="h-3 w-3 shrink-0 ml-auto text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[calc(100vw-2rem)] md:max-w-[700px] w-full p-0 gap-0 overflow-hidden flex flex-col max-h-[85vh]"
        aria-describedby="context-picker-description"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Switch Workspace</DialogTitle>
              <DialogDescription id="context-picker-description">
                Choose an agency and client. Use arrow keys to navigate.
              </DialogDescription>
            </div>
            {hasRole("admin") && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2 text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={handleAdminMode}
              >
                <Shield className="h-4 w-4" />
                Admin View
              </Button>
            )}
          </div>
        </DialogHeader>

        {recentSelections.length > 0 && (
          <div className="px-4 py-2 border-b bg-muted/30">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Recent</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recentSelections.map((selection) => (
                <button
                  key={selection.clientId}
                  type="button"
                  onClick={() => handleRecentSelect(selection)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-background border hover:bg-accent transition-colors"
                >
                  <Briefcase className="h-3 w-3 text-muted-foreground" />
                  <span className="font-medium">{selection.clientName}</span>
                  <span className="text-muted-foreground">({selection.orgName})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row flex-1 min-h-0">
          {/* Left: Agency list */}
          <div className="w-full md:w-[280px] md:border-r shrink-0 flex flex-col border-b md:border-b-0">
            <div className="px-3 py-2 border-b md:border-b-0 shrink-0">
              <p className="text-xs font-medium text-muted-foreground">Agency</p>
            </div>
            <ScrollArea className="h-[200px] md:h-[320px]">
              <div className="p-1 space-y-0.5">
                {visibleOrganizations.map((org, index) => {
                  const orgClientCount = clients.filter(
                    (c) => c.org_id === org.id
                  ).length;
                  const isSelected = currentOrg?.id === org.id;
                  const isFocused = activePanel === 'org' && focusedOrgIndex === index;
                  return (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => handleOrgSelect(org)}
                      onMouseEnter={() => {
                        if (activePanel === 'org') setFocusedOrgIndex(index);
                      }}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm transition-colors ${
                        isSelected
                          ? "bg-primary/10 text-primary font-medium"
                          : isFocused
                          ? "bg-accent"
                          : "hover:bg-accent/50"
                      }`}
                    >
                      <Building2 className="h-3.5 w-3.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <span className="block truncate">{org.name}</span>
                        <span
                          className={`text-xs ${
                            isSelected ? "text-primary/70" : "text-muted-foreground"
                          }`}
                        >
                          {orgClientCount}{" "}
                          {orgClientCount === 1 ? "client" : "clients"}
                        </span>
                      </div>
                      {isSelected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Right: Client list */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {!currentOrg ? (
              <div className="flex flex-col items-center justify-center px-4 text-center h-[200px] md:h-[320px]">
                <Briefcase className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Select an agency to choose a client
                </p>
              </div>
            ) : agencyClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 text-center h-[200px] md:h-[320px]">
                <Briefcase className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm font-medium">No clients yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Create your first client to get started
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 mt-3"
                  onClick={() => {
                    setOpen(false);
                    navigate("/agency-management");
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Client
                </Button>
              </div>
            ) : (
              <>
                <div className="p-3 border-b space-y-2 shrink-0">
                  <p className="text-xs font-medium text-muted-foreground truncate">
                    {currentOrg.name}
                  </p>
                  <Input
                    ref={searchInputRef}
                    placeholder="Search clients... (↑↓ to navigate)"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setFocusedClientIndex(0);
                    }}
                    onFocus={() => setActivePanel('client')}
                    className="h-8"
                  />
                </div>
                <ScrollArea className="h-[200px] md:h-[320px]">
                  <div className="p-2 space-y-0.5">
                    {filteredClients.map((client, index) => {
                      const isSelected = currentClient?.id === client.id;
                      const isFocused = activePanel === 'client' && focusedClientIndex === index;
                      return (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          onMouseEnter={() => {
                            if (activePanel === 'client') setFocusedClientIndex(index);
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm transition-colors ${
                            isSelected
                              ? "bg-primary/10 text-primary font-medium"
                              : isFocused
                              ? "bg-accent"
                              : "hover:bg-accent/50"
                          }`}
                        >
                          <Briefcase className="h-3.5 w-3.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <span className="block truncate">{client.name}</span>
                            <span
                              className={`text-xs capitalize ${
                                isSelected
                                  ? "text-primary/70"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {client.industry.replace(/_/g, " ")}
                            </span>
                          </div>
                          {isSelected && <Check className="h-4 w-4 shrink-0" />}
                        </button>
                      );
                    })}
                    {filteredClients.length === 0 && clientSearch && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        No clients matching &quot;{clientSearch}&quot;
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
